import type { IncomeCategory } from 'src/services/incomeTypes/incomeCategory';
import { getIncomeYears } from 'src/pages/loan/income/data';
import type { IncomeXpertAnalysisDto } from './incomeXpertAnalysis';
import type {
  BorrowerScope,
  IncomeSource,
  IncomeSourceStatus,
} from 'src/pages/loan/income/data';

/**
 * The wire shape of `income_json`: every `createForm` field, FLAT, keyed by
 * its form_json field name -- this is exactly what mos-mvp's
 * validateIncomePayload.ts checks against the tenant's form_json field list.
 * `years` (the UI-facing `Record<absoluteYear, number>`) is not itself a
 * field name; it round-trips through the `yr1`/`yr2` naming convention (see
 * AddIncomeSheet.tsx's COMMON_FIELD_NAMES comment and
 * docs/income-types-configuration-guide.md).
 */
export type LoanIncomePayloadDto = Record<string, unknown>;

/** A row as returned by GET /loans/{loanId}/incomes. */
export interface LoanIncomeDto {
  id: string;
  loanId: string;
  borrowerPairId?: string | null;
  scope?: BorrowerScope | null;
  /** The source system's record id for an imported stream -- for Encompass,
   *  the employment record an `[los:]` employment field is matched to. */
  externalIncomeId?: string | null;
  /** Empty (or null) for a stream imported without a type -- a self-employed
   *  VOE or other-income VOOI record -- which the user completes via Edit. */
  incomeType: string | null;
  /** The stream's own category code; follows the type once one is set. */
  incomeCategory?: IncomeCategory | null;
  incomeJson: LoanIncomePayloadDto;
  analysisJson?: Record<string, unknown>;
  /** IncomeXpert's underwriting advisories for this stream, refreshed on each
   *  push to the vendor. Empty until the stream has a vendor income. */
  incomeXpertAdvisories?: string[];
  /** IncomeXpert's income analysis, read on the same push. Null until the
   *  stream has a vendor income. */
  incomeXpertAnalysis?: IncomeXpertAnalysisDto | null;
  /** Why the last push to IncomeXpert did nothing (`unmapped_income_type`,
   *  `no_employer`, `push_failed`, ...). Null once the stream reaches the
   *  vendor; see services/income/incomeXpertSyncStatus.ts. */
  incomeXpertSyncStatus?: string | null;
}

/** The common columns every income type's create form shares -- typed on
 *  `IncomeSource` because the Income Summary grid sorts/totals on them.
 *  Everything else configured lands in `IncomeSource.fields`. */
const COMMON_KEYS = [
  'employer',
  'position',
  'startDate',
  'endDate',
  'payFrequency',
  'monthly',
  'statedMonthlyIncome',
  'ytd',
  'yr1',
  'yr2',
  'status',
  'trend',
  'supportingDocumentTypeIds',
] as const;

/** Server row -> the flat shape the grid and sheet work with. */
export const toIncomeSource = (dto: LoanIncomeDto): IncomeSource => {
  const json = dto.incomeJson ?? {};
  const [priorYear, priorYear2] = getIncomeYears();

  const years: Record<string, number> = {};
  if (json.yr1 !== undefined && json.yr1 !== null)
    years[String(priorYear)] = Number(json.yr1) || 0;
  if (json.yr2 !== undefined && json.yr2 !== null)
    years[String(priorYear2)] = Number(json.yr2) || 0;

  const fields: Record<string, unknown> = {};
  const rawPayFrequency =
    (typeof json.currentPayFrequency === 'string' &&
      json.currentPayFrequency.trim()) ||
    (typeof json.payFrequency === 'string' && json.payFrequency.trim()) ||
    '';

  for (const [key, value] of Object.entries(json)) {
    if (!(COMMON_KEYS as readonly string[]).includes(key)) fields[key] = value;
  }
  if (rawPayFrequency) {
    fields.currentPayFrequency =
      (typeof fields.currentPayFrequency === 'string' &&
        fields.currentPayFrequency.trim()) ||
      rawPayFrequency;
  }

  return {
    id: dto.id,
    type: dto.incomeType ?? '',
    incomeCategory: dto.incomeCategory ?? null,
    borrowerPairId: dto.borrowerPairId ?? null,
    scope: dto.scope ?? null,
    externalIncomeId: dto.externalIncomeId ?? null,
    employer: String(json.employer ?? ''),
    position: String(json.position ?? ''),
    startDate: String(json.startDate ?? ''),
    ...(json.endDate ? { endDate: String(json.endDate) } : {}),
    payFrequency: rawPayFrequency,
    monthly: Number(json.monthly) || 0,
    // What the borrower says they earn, beside what the documents compute.
    // A create-form field on every income type, so it travels in income_json
    // like `monthly` rather than as a column of its own.
    statedMonthlyIncome: Number(json.statedMonthlyIncome) || 0,
    ytd: Number(json.ytd) || 0,
    years,
    status: (json.status as IncomeSourceStatus) ?? 'pending',
    trend: resolveStreamTrend(json.trend, dto.incomeXpertAnalysis),
    supportingDocumentTypeIds: Array.isArray(json.supportingDocumentTypeIds)
      ? (json.supportingDocumentTypeIds as string[])
      : [],
    fields,
    analysisTables: (dto.analysisJson ?? {}) as IncomeSource['analysisTables'],
    incomeXpertAdvisories: dto.incomeXpertAdvisories ?? [],
    incomeXpertAnalysis: dto.incomeXpertAnalysis ?? null,
    incomeXpertSyncStatus: dto.incomeXpertSyncStatus ?? null,
  };
};

/** Parses a percentage string or number into a clean float, e.g. "-35.46%" -> -35.46 */
const parseTrendPercentage = (raw: unknown): number | null => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'string') return null;
  const cleaned = raw.replaceAll('%', '').replaceAll(',', '').trim();
  if (!cleaned) return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
};

/** Extracts trend percentage from IncomeXpert analysis when income_json has 0 or null */
export const extractTrendFromAnalysis = (
  analysis?: IncomeXpertAnalysisDto | null,
): number | null => {
  if (!analysis) return null;

  // 1. Root TrendPercentage (non-salary streams like 1099, rental, etc.)
  const rootTrend = parseTrendPercentage(analysis.TrendPercentage);
  if (rootTrend !== null) return rootTrend;

  // 2. Component TrendPercentage (salary streams like W-2, Military Pay: Base, W2, or first available)
  const components = analysis.SalaryComponents ?? [];
  const primary =
    components.find((c) => {
      const t = (c.ComponentType ?? '').trim().toLowerCase();
      return (
        (t === 'base' || t === 'w2') &&
        parseTrendPercentage(c.TrendPercentage) !== null
      );
    }) ??
    components.find((c) => parseTrendPercentage(c.TrendPercentage) !== null);

  return primary ? parseTrendPercentage(primary.TrendPercentage) : null;
};

export const resolveStreamTrend = (
  storedTrend: unknown,
  analysis?: IncomeXpertAnalysisDto | null,
): number => {
  const stored =
    storedTrend !== undefined && storedTrend !== null
      ? Number(storedTrend)
      : Number.NaN;
  if (Number.isFinite(stored) && stored !== 0) {
    return stored;
  }
  const fromAnalysis = extractTrendFromAnalysis(analysis);
  if (fromAnalysis !== null) {
    return fromAnalysis;
  }
  return Number.isFinite(stored) ? stored : 0;
};

/**
 * The flat UI shape -> the request body. `id`, `loanId` and the borrower
 * columns live outside income_json, so they are stripped from the payload
 * here, and every entry in `fields` is spread in as-is.
 *
 * `endDate`, `payFrequency`, `yr1` and `yr2` are NOT sent: the per-category
 * create form retired them, so no income type declares them any more. They are
 * still READ by `toIncomeSource` above, so a row saved against an older form
 * still shows what it holds -- it just stops being written back, and the
 * server drops the stored key on that save (see RETIRED_FIELDS in
 * api/tenant/loanIncomes/validateIncomePayload.ts).
 */
export const toLoanIncomePayload = (
  row: IncomeSource,
): LoanIncomePayloadDto => {
  return {
    employer: row.employer,
    position: row.position,
    startDate: row.startDate,
    monthly: row.monthly,
    statedMonthlyIncome: row.statedMonthlyIncome,
    ytd: row.ytd,
    status: row.status,
    trend: row.trend,
    supportingDocumentTypeIds: row.supportingDocumentTypeIds,
    ...row.fields,
  };
};
